import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { loginHistoryApi } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import { Activity, Monitor, Smartphone, RefreshCw, Clock, ChevronDown, ChevronUp } from 'lucide-react'

interface LoginEntry {
  id: number
  user_id: number
  user_name?: string
  user_email?: string
  device_name?: string
  ip_address?: string
  app_version?: string
  login_at: string
  success: boolean
}

interface UserGroup {
  user_id: number
  user_name: string
  user_email: string
  sessions: LoginEntry[]
  last_login: string
  success_count: number
}

export default function ActivityPage() {
  const [expandedUser, setExpandedUser] = useState<number | null>(null)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['login-history'],
    queryFn: () => loginHistoryApi.list({ page: 1, page_size: 500 }),
    refetchInterval: 30000,
  })

  const entries: LoginEntry[] = data?.data ?? []

  const grouped = entries.reduce((acc: Record<number, UserGroup>, e) => {
    if (!acc[e.user_id]) {
      acc[e.user_id] = {
        user_id: e.user_id,
        user_name: e.user_name || '—',
        user_email: e.user_email || '',
        sessions: [],
        last_login: e.login_at,
        success_count: 0,
      }
    }
    acc[e.user_id].sessions.push(e)
    if (new Date(e.login_at) > new Date(acc[e.user_id].last_login)) {
      acc[e.user_id].last_login = e.login_at
    }
    if (e.success) acc[e.user_id].success_count++
    return acc
  }, {})

  const users = Object.values(grouped).sort(
    (a, b) => new Date(b.last_login).getTime() - new Date(a.last_login).getTime()
  )

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="h-6 w-6 text-primary-600" /> Atividade de Login
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {users.length} utilizador{users.length !== 1 ? 'es' : ''} · {entries.length} sessões · atualiza cada 30s
          </p>
        </div>
        <button onClick={() => refetch()}
          className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
          <RefreshCw className="h-4 w-4" /> Atualizar
        </button>
      </div>

      <div className="mt-6 card overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <Monitor className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p>Sem registos de login ainda.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                <th className="pb-2 pr-4 font-medium">Utilizador</th>
                <th className="pb-2 pr-4 font-medium">Último acesso</th>
                <th className="pb-2 pr-4 font-medium">Sessões</th>
                <th className="pb-2 text-right font-medium">Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <Fragment key={u.user_id}>
                  <tr className="border-b border-gray-50 hover:bg-gray-50 text-gray-700">
                    <td className="py-2.5 pr-4">
                      <span className="font-medium text-gray-900">{u.user_name}</span>
                      {u.user_email && (
                        <span className="ml-2 text-xs text-gray-400">{u.user_email}</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-gray-500 whitespace-nowrap">
                      {formatDateTime(u.last_login)}
                    </td>
                    <td className="py-2.5 pr-4 text-xs">
                      <span className="text-gray-500">{u.sessions.length} sessões</span>
                      <span className="ml-2 text-green-600">{u.success_count} ok</span>
                      {u.sessions.length - u.success_count > 0 && (
                        <span className="ml-1 text-red-500">{u.sessions.length - u.success_count} falhadas</span>
                      )}
                    </td>
                    <td className="py-2.5 text-right">
                      <button
                        onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                        title="Ver sessões"
                        className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors ${
                          expandedUser === u.user_id
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-400 hover:text-primary-600 hover:bg-gray-100'
                        }`}
                      >
                        <Clock className="h-3.5 w-3.5" />
                        <span>Sessões</span>
                        {expandedUser === u.user_id
                          ? <ChevronUp className="h-3 w-3" />
                          : <ChevronDown className="h-3 w-3" />}
                      </button>
                    </td>
                  </tr>
                  {expandedUser === u.user_id && (
                    <tr>
                      <td colSpan={4} className="pb-3 pt-0 bg-gray-50/60">
                        <div className="mx-2 rounded border border-gray-100 overflow-hidden">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-gray-400 border-b border-gray-100 bg-gray-50">
                                <th className="px-3 py-1.5 font-medium">Dispositivo</th>
                                <th className="px-3 py-1.5 font-medium">IP</th>
                                <th className="px-3 py-1.5 font-medium">Versão</th>
                                <th className="px-3 py-1.5 font-medium">Data / Hora</th>
                                <th className="px-3 py-1.5 font-medium">Estado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {u.sessions.map((s) => (
                                <tr key={s.id} className="border-b border-gray-50 hover:bg-white">
                                  <td className="px-3 py-1.5">
                                    <div className="flex items-center gap-1">
                                      <Smartphone className="h-3 w-3 text-gray-400 shrink-0" />
                                      <span>{s.device_name || '—'}</span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-1.5 font-mono text-gray-500">{s.ip_address || '—'}</td>
                                  <td className="px-3 py-1.5 text-gray-500">{s.app_version || '—'}</td>
                                  <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">{formatDateTime(s.login_at)}</td>
                                  <td className="px-3 py-1.5">
                                    <span className={`inline-flex rounded-full px-1.5 py-0.5 font-medium ${
                                      s.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                                    }`}>
                                      {s.success ? '✓ OK' : '✗ Falhou'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
